import { useContext, useRef, useState, useEffect, useCallback } from 'react';
import { TextButton, FocusText, Spinner } from 't-lib-ui/widgets';
import { FlowForm, Stack, useFlowFormGlobal, FlowFormLeaveContext, get2DKeyHandler } from 't-lib-ui/structure';
import { RouterContext, goTo } from 't-lib-ui/router';
import { sidebarLeaveHandler } from '../components/LeftSidebarFocus';
import MainLayout from '../components/MainLayout';
import PlusIcon from '../components/PlusIcon';
import MailIcon from '../components/MailIcon';
import CheckmarkCircleIcon from '../components/CheckmarkCircleIcon';
import PageHeader from '../components/PageHeader';
import HeaderAuthButtons from '../components/HeaderAuthButtons';
import PageIntro from '../components/PageIntro';
import DiscoverIcon from '../components/DiscoverIcon';
import FeedIcon from '../components/FeedIcon';
import { getFeedIconUrl } from '../schema-copy/feed-icon';
import { CategoryIcon } from '../components/CategoryIcons';
import { formatCategory } from '../utils/formatting';
import { AppState } from '../context/general';
import PreviewFeedPopup from '../components/PreviewFeedPopup';
import FeedQuotaNote from '../components/FeedQuotaNote';
import { useFeedQuota } from '../connector/feed-quota';
import { getPublicCatalog, useGetDiscoverContent } from '../connector/api';
import { getPlanLimits } from '../schema-copy/userPlans';
import { globalFocusQuery } from 't-lib-ui/helpers';
import { DiscoverCollection, RestCatalog } from '../schema-copy/api-schema';
import { getCdnUrl } from '../connector/auth';
import { flushPendingPreferences } from '../connector/preferences';
import FeedQuotaDialog from '../components/FeedQuotaDialog';
import './DiscoverPage.css';

const DiscoverPage: React.FC = () => {
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  const cdnUrl = getCdnUrl(appState.state.authData);
  const { feedEngine } = appState;
  const [processingFeeds, setProcessingFeeds] = useState<Set<string>>(new Set());
  const [previewFeed, setPreviewFeed] = useState<RestCatalog | null>(null);
  const [expandedCollection, setExpandedCollection] = useState<string | null>(null);
  const [showQuotaDialog, setShowQuotaDialog] = useState(false);
  const canUseNewsletters = getPlanLimits(appState.state.currentUser?.userPlan ?? 'free').maxNewsletterFeeds > 0;
  const { atLimit: atFeedQuota } = useFeedQuota();

  const { data: discoverContent } = useGetDiscoverContent(feedEngine?.getDiscoverDate());

  // Subscription state comes from the feed engine (works for both logged-in and anonymous mode);
  // the anon engine has no `currentUser`, so reading currentUser.feeds would never reflect follows.
  const [subscribedFeedIds, setSubscribedFeedIds] = useState<Set<string>>(() => new Set(feedEngine.getFeeds().map(f => f.id)));
  useEffect(() => {
    const update = () => setSubscribedFeedIds(new Set(feedEngine.getFeeds().map(f => f.id)));
    update();
    return feedEngine.onFeedsChange(update);
  }, [feedEngine]);
  const isFeedSubscribed = useCallback((feedId: string) =>
    subscribedFeedIds.has(feedId) || feedEngine.isRecentlySubscribed(feedId)
  , [subscribedFeedIds, feedEngine]);

  const orderedCollectionsRef = useRef(new Map<string, DiscoverCollection['feedIds']>());

  const handleFeedAdded = (_feedId: string) => {
    // Stay on the page - user can click "View" button to navigate to the feed
  };
    useFlowFormGlobal();


  const handleFeedAction = async (feedId: string, isSubscribed: boolean) => {
    if (!feedEngine) return;

    setProcessingFeeds(prev => new Set(prev).add(feedId));

    try {
      const minDelayPromise = new Promise(resolve => setTimeout(resolve, 750));

      if (isSubscribed) {
        await feedEngine.unsubscribe(feedId);
      } else {
        await flushPendingPreferences();
        await feedEngine.subscribe(feedId);
      }

      await minDelayPromise;
    } catch (error) {
      if (error && typeof error == 'object' && 'type' in error && (error as any).type == 'Quota')
        setShowQuotaDialog(true);
      else
        console.error('Subscription action failed:', error);
    } finally {
      setProcessingFeeds(prev => {
        const next = new Set(prev);
        next.delete(feedId);
        return next;
      });
    }
  };

  // Collapse a collection from inside the card (Esc): the expanded feed buttons unmount,
  // so move focus to the persistent footer toggle (now showing "show all").
  const collapseCollection = (collectionId: string) => {
    setExpandedCollection(null);
    globalFocusQuery(`#collectionCard-${collectionId} .discoverCollectionShowAll .flowFocussable`);
  };

  const handlePreview = async (feedId: string) => {
    try {
      // getPublicCatalog works for both logged-in and anonymous users (public endpoint).
      const catalog = await getPublicCatalog(feedId, appState.state.authData);
      setPreviewFeed(catalog as RestCatalog);
    } catch (error) {
      console.error('Error loading feed for preview:', error);
    }
  };

  const handleFollowAll = async (feedIds: string[]) => {
    if (!feedEngine) return;

    const unsubscribedFeeds = feedIds.filter(feedId => !isFeedSubscribed(feedId));

    if (!unsubscribedFeeds.length) return;

    // Mark all as processing
    setProcessingFeeds(prev => {
      const next = new Set(prev);
      unsubscribedFeeds.forEach(id => next.add(id));
      return next;
    });

    try {
      const minDelayPromise = new Promise(resolve => setTimeout(resolve, 750));

      // Flush preferences before subscribing
      await flushPendingPreferences();

      // Subscribe to all unsubscribed feeds
      await Promise.all(
        unsubscribedFeeds.map(feedId => feedEngine.subscribe(feedId))
      );

      await minDelayPromise;
    } catch (error) {
      if (error && typeof error == 'object' && 'type' in error && (error as any).type == 'Quota')
        setShowQuotaDialog(true);
      else
        console.error('Follow all action failed:', error);
    } finally {
      // Clear processing state
      setProcessingFeeds(prev => {
        const next = new Set(prev);
        unsubscribedFeeds.forEach(id => next.delete(id));
        return next;
      });
    }
  };

  const featuredFeeds = discoverContent?.featuredFeeds || [];
  const collections = discoverContent?.collections || [];
  const isLoading = !discoverContent;

  return (
    <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
    <MainLayout
      selectedFeed="discover"
      right={({ onExpand }) => (
        <div className="discoverPage">
          <PageHeader title="Discover" onMenu={onExpand} rightContent={<HeaderAuthButtons />} />

          <div className="discoverIntro">
            <PageIntro
              icon={<DiscoverIcon size={24} />}
              title="Discover feeds"
              subtitle="Browse hand-picked feeds and curated collections, refreshed daily."
            />
          </div>

          {/* One FlowForm spans all sections so 2D spatial navigation (the cards are a
              2-column grid) can cross between Featured, Collections, and the CTA row. */}
          <FlowForm main className="discoverContent">
          {atFeedQuota && (
            <div className="feedQuotaBanner"><FeedQuotaNote /></div>
          )}
          {/* Featured Feeds Section */}
          <div className="discoverFeatured">
            <div className="discoverFeaturedContent">
              <div className="discoverFeaturedGrid">
                {isLoading ? (
                  Array.from({ length: 4 }).map((_, index) => (
                    <div key={`skeleton-featured-${index}`} className="discoverFeaturedCard skeleton">
                      <div className="discoverFeaturedCardImage skeleton-shimmer" />
                      <div className="discoverFeaturedCardContent">
                        <div className="skeleton-text skeleton-title skeleton-shimmer" />
                        <div className="skeleton-text skeleton-description skeleton-shimmer" />
                        <div className="skeleton-text skeleton-description skeleton-shimmer" />
                      </div>
                      <div className="discoverFeaturedCardFooter">
                        <div className="skeleton-text skeleton-category skeleton-shimmer" />
                        <div className="skeleton-text skeleton-button skeleton-shimmer" />
                      </div>
                    </div>
                  ))
                ) : (
                  featuredFeeds.map((feed) => {
                    const isSubscribed = isFeedSubscribed(feed.id);
                    const isProcessing = processingFeeds.has(feed.id);

                    return (
                      <div key={feed.id} className="discoverFeaturedCard">
                        <div className="discoverFeaturedCardImage">
                          {feed.headerImage ? (
                            <img
                              src={`${cdnUrl}images/${feed.headerImage}-w600.webp`}
                              srcSet={`${cdnUrl}images/${feed.headerImage}-w400.webp 400w, ${cdnUrl}images/${feed.headerImage}-w600.webp 600w`}
                              sizes="(max-width: 768px) 100vw, 600px"
                              alt={feed.title}
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = `${cdnUrl}images/${feed.headerImage}.png`;
                              }}
                            />
                          ) : (
                            <span>📄</span>
                          )}
                        </div>
                        <div className="discoverFeaturedCardContent">
                          <div className="discoverFeaturedCardHeader">
                            <FeedIcon
                              className="discoverFeaturedCardFavicon"
                              src={getFeedIconUrl(feed, 96)}
                              circular={feed.circularIcon}
                            />
                            <div className="discoverFeaturedCardTitleGroup">
                              <h3 className="discoverFeaturedCardTitle">{feed.title}</h3>
                              {feed.siteUrl && (
                                <a
                                  className="discoverFeaturedCardDomain flowFocussable flow2d"
                                  href={feed.siteUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={get2DKeyHandler((el: any) => el.click()) as any}
                                >
                                  {new URL(feed.siteUrl).hostname.replace(/^www\d?\./, '')}
                                </a>
                              )}
                            </div>
                          </div>
                          <p className="discoverFeaturedCardDescription">{feed.catalogDescription || feed.description}</p>
                        </div>
                        <div className="discoverFeaturedCardFooter">
                          <div className="discoverFeaturedCardCategory">
                            <CategoryIcon category={feed.category || 'Other'} size={16} color="var(--gray-400)" />
                            <span>{formatCategory((feed.category || 'Other').replace(/\/.*/, ''))}</span>
                          </div>
                          <div className="discoverFeaturedCardActions">
                            <Stack>
                              <Spinner spinning={isProcessing} className={isProcessing ? 'show' : ''} />
                              <CheckmarkCircleIcon className={isSubscribed && !isProcessing ? 'show' : ''} />
                            </Stack>
                            <TextButton
                              label={isProcessing ? 'working' : isSubscribed ? "Unfollow" : "+ Follow"}
                              type="major"
                              size="small"
                              focussable="flow2d"
                              enabled={isProcessing || (atFeedQuota && !isSubscribed) ? "disabled" : "enabled"}
                              onClick={() => handleFeedAction(feed.id, isSubscribed)}
                            />
                            <TextButton
                              label="Preview"
                              type="minor"
                              size="small"
                              focussable="flow2d"
                              onClick={() => handlePreview(feed.id)}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Collections Section */}
          <div className="discoverCollections">
            <div className="discoverCollectionsContent">
              <div className="discoverCollectionsGrid">
                {isLoading ? (
                  Array.from({ length: 4 }).map((_, index) => (
                    <div key={`skeleton-collection-${index}`} className="discoverCollectionCard skeleton">
                      <div className="discoverCollectionIcon skeleton-shimmer" />
                      <div className="discoverCollectionHeader">
                        <div className="skeleton-text skeleton-title skeleton-shimmer" style={{ flex: 1 }} />
                        <div className="skeleton-text skeleton-button skeleton-shimmer" />
                      </div>
                      <div className="skeleton-text skeleton-description skeleton-shimmer" style={{ margin: '0.25rem 1rem 0.75rem' }} />
                      <div className="skeleton-text skeleton-description skeleton-shimmer" style={{ margin: '0 1rem 0.75rem' }} />
                      <div className="discoverCollectionFeeds">
                        {Array.from({ length: 3 }).map((_, feedIndex) => (
                          <div key={`skeleton-feed-${feedIndex}`} className="discoverCollectionFeed">
                            <div className="skeleton-text skeleton-icon skeleton-shimmer" />
                            <div className="skeleton-text skeleton-feed-title skeleton-shimmer" style={{ flex: 1 }} />
                            <div className="skeleton-text skeleton-button skeleton-shimmer" />
                          </div>
                        ))}
                      </div>
                      <div className="discoverCollectionShowAll">
                        <div className="skeleton-text skeleton-category skeleton-shimmer" />
                      </div>
                    </div>
                  ))
                ) : (
                  collections.map((collection) => {
                    const isExpanded = expandedCollection == collection.id;
                    const collectionFeedIds = collection.feedIds.map(f => f.feedId);
                    const allSubscribed = collectionFeedIds.every(feedId => isFeedSubscribed(feedId));
                    const anyProcessing = collectionFeedIds.some(feedId => processingFeeds.has(feedId));

                    // Prioritize unsubscribed feeds: show up to 3 unsubscribed first, then the rest in original order
                    let orderedFeeds = orderedCollectionsRef.current.get(collection.id);
                    if (!orderedFeeds) {
                      const hasSubscribedFeeds = collection.feedIds.slice(0, 3).find(
                        feed => isFeedSubscribed(feed.feedId)
                      ) !== undefined;
                      const threeUnsubscribed = collection.feedIds.filter(
                        feed => !isFeedSubscribed(feed.feedId)
                      ).slice(0, 3);
                      const l = hasSubscribedFeeds
                        ? [...threeUnsubscribed, ...collection.feedIds.filter(feed => !threeUnsubscribed.find(f => f.feedId == feed.feedId)) ]
                        : collection.feedIds;
                      orderedFeeds = l;
                      if (l.length)
                        orderedCollectionsRef.current.set(collection.id, l);
                    }

                    return (
                      <div
                        key={collection.id}
                        id={'collectionCard-' + collection.id}
                        className={`discoverCollectionCard ${isExpanded ? 'expanded' : ''}`}
                        onClick={(e) => {
                          if (!(e.target as HTMLElement).closest('.discoverCollectionFeedActions, .discoverCollectionTitle, .discoverCollectionFollowAll')) {
                            setExpandedCollection(isExpanded ? null : collection.id);
                          }
                        }}
                        // Esc collapses an expanded card while focus is on any element inside it.
                        onKeyDown={isExpanded ? (e) => {
                          if (e.key == 'Escape') { e.stopPropagation(); collapseCollection(collection.id); }
                        } : undefined}
                      >
                        <div className="discoverCollectionIcon">
                          <img
                            src={`${cdnUrl}images/${collection.image}-w600.webp`}
                            srcSet={`${cdnUrl}images/${collection.image}-w400.webp 400w, ${cdnUrl}images/${collection.image}-w600.webp 600w`}
                            sizes="(max-width: 768px) 100vw, 600px"
                            alt={collection.title}
                          />
                        </div>
                        <div className="discoverCollectionHeader">
                          <h3
                            className="discoverCollectionTitle"
                            onClick={(e) => {
                              if (isExpanded) {
                                e.stopPropagation();
                                setExpandedCollection(null);
                              }
                            }}
                            style={{ cursor: isExpanded ? 'pointer' : 'default' }}
                          >
                            {collection.title}
                          </h3>
                          <div className="discoverCollectionFollowAll" onClick={(e) => e.stopPropagation()}>
                            <Stack>
                              <Spinner spinning={anyProcessing} className={anyProcessing ? 'show' : ''} />
                              <CheckmarkCircleIcon className={allSubscribed && !anyProcessing ? 'show' : ''} />
                            </Stack>
                            <TextButton
                              label="+ Follow All"
                              type="major"
                              size="small"
                              focussable="flow2d"
                              enabled={allSubscribed || anyProcessing || atFeedQuota ? "disabled" : "enabled"}
                              onClick={() => handleFollowAll(collectionFeedIds)}
                            />
                          </div>
                        </div>
                        <p className="discoverCollectionDescription">{collection.description}</p>
                        <div className="discoverCollectionFeeds" id={'collection-' + collection.id}>
                          {orderedFeeds.map((feed) => {
                            const isSubscribed = isFeedSubscribed(feed.feedId);
                            const isProcessing = processingFeeds.has(feed.feedId);

                            return (
                              <div key={feed.feedId} className="discoverCollectionFeed">
                                <FeedIcon
                                  className="discoverCollectionFeedIcon"
                                  src={feed.iconUrl}
                                  circular={feed.circularIcon}
                                />
                                <a
                                  className={`discoverCollectionFeedTitle discoverCollectionFeedTitleLink flowFocussable flow2d`}
                                  href={feed.siteUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={get2DKeyHandler((el: any) => el.click()) as any}
                                >
                                  {feed.title}
                                </a>
                                <div
                                  className="discoverCollectionFeedActions"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Stack>
                                    <Spinner spinning={isProcessing} className={isProcessing ? 'show' : ''} />
                                    <CheckmarkCircleIcon className={isSubscribed && !isProcessing ? 'show' : ''} />
                                  </Stack>
                                  <TextButton
                                    label={isProcessing ? 'working' : isSubscribed ? "Unfollow" : "+ Follow"}
                                    type="major"
                                    size="small"
                                    enabled={isProcessing || (atFeedQuota && !isSubscribed) ? "disabled" : "enabled"}
                                    focussable='flow2d'
                                    onClick={() => handleFeedAction(feed.feedId, isSubscribed)}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {/* One persistent footer toggle: the same FocusText switches its
                            label between "show all" and "close" instead of swapping two
                            elements, so keyboard focus stays on it across expand/collapse. */}
                        <div className="discoverCollectionShowAll">
                          {!isExpanded && <span key="count" className="discoverCollectionShowAllCount">{collection.feedIds.length} feeds</span>}
                          {(isExpanded || collection.feedIds.length > 3) && <FocusText
                            key="toggle"
                            underline={true}
                            focussable="flow2d"
                            onClick={() => setExpandedCollection(isExpanded ? null : collection.id)}
                          >
                            {isExpanded ? 'close' : 'show all'}
                          </FocusText>}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Call-to-action cards: add a feed yourself, or create a newsletter inbox */}
          <div className="discoverBrowse">
            <div className="discoverBrowseContent">
              <button
                className="addFeedNewsletterCta flowFocussable flow2d"
                tabIndex={0}
                onKeyDown={get2DKeyHandler((el: any) => el.click()) as any}
                onClick={() => goTo(routerCtx, '/a/add')}
              >
                <span className="addFeedNewsletterCtaIcon"><PlusIcon size={26} /></span>
                <span className="addFeedNewsletterCtaText">
                  <span className="addFeedNewsletterCtaTitle">Find more feeds</span>
                  <span className="addFeedNewsletterCtaDesc">Search for any site, or paste a feed URL.</span>
                </span>
              </button>
              {canUseNewsletters && (
              <button
                className="addFeedNewsletterCta flowFocussable flow2d"
                tabIndex={0}
                onKeyDown={get2DKeyHandler((el: any) => el.click()) as any}
                onClick={() => goTo(routerCtx, '/a/add/newsletter')}
              >
                <span className="addFeedNewsletterCtaIcon"><MailIcon size={26} /></span>
                <span className="addFeedNewsletterCtaText">
                  <span className="addFeedNewsletterCtaTitle">Read email newsletters here</span>
                  <span className="addFeedNewsletterCtaDesc">Create a private inbox and get newsletters as a feed.</span>
                </span>
              </button>
              )}
            </div>
          </div>
          </FlowForm>

          <PreviewFeedPopup
            show={!!previewFeed}
            feed={previewFeed}
            onClose={() => setPreviewFeed(null)}
            subscribed={previewFeed ? isFeedSubscribed(previewFeed.id) : false}
            onSubscribe={async () => {
              if (previewFeed && feedEngine) {
                try {
                  await flushPendingPreferences();
                  const result = await feedEngine.subscribe(previewFeed.id);
                  if (result) {
                    handleFeedAdded(previewFeed.id);
                    setPreviewFeed(null);
                  }
                } catch (e) {
                  if (e && typeof e == 'object' && 'type' in e && (e as any).type == 'Quota') {
                    setPreviewFeed(null);
                    setShowQuotaDialog(true);
                  }
                }
              }
            }}
          />
          <FeedQuotaDialog show={showQuotaDialog} onClose={() => setShowQuotaDialog(false)} />
        </div>
      )}
      startWithLeftCollapsed={false}
    />
    </FlowFormLeaveContext.Provider>
  );
};

export default DiscoverPage;
